import { Mail, Phone, Navigation, Ship, Info } from "lucide-react";

const FERRIES = [
  {
    id: "waxholm",
    name: "Waxholmsbolaget",
    phone: "+46 8 600 10 00",
    url: "https://waxholmsbolaget.se/",
    blurb:
      "Year-round archipelago boats from Strömkajen, Nacka Strand, Lidingö, Vaxholm and Värmdö. Free with an SL period card during autumn, winter and spring.",
  },
  {
    id: "cinderella",
    name: "Cinderellabåtarna",
    phone: "+46 8 12 00 40 45",
    url: "https://www.stromma.com/sv-se/stockholm/cinderellabatarna/tidtabeller/",
    blurb:
      "Multiple daily summer departures from Strömkajen with pre-bookable seats. The fastest scheduled route from central Stockholm.",
  },
];

const CHARTERS = [
  { name: "Öppet Hav · RIBs, helicopter & activities", url: "https://oppethav.se/" },
  { name: "RIB Stockholm · pickup from central Stockholm", url: "https://www.ribstockholm.com/home-svenska/" },
  { name: "Sea Event · charter & event boats", url: "https://seaevents.se/" },
  { name: "Gällnö Sjötaxi · taxi-boat", url: "http://rederigallno.se/" },
  { name: "Kronudden Taxi & Charter", url: "http://www.kronudden.com/" },
  { name: "Waxholm Water Taxi", url: "http://waxholmwatertaxi.com/" },
  { name: "Gustafs Taxibåtar & Events Stockholm", url: "https://boka.se/gustavustaxibatarEventStockholm" },
  { name: "Catch & Relax · sport-fishing transfer", url: "http://www.catchrelax.se/catchrelax/Fiskeguide.html" },
  { name: "Out · sailing experiences", url: "https://www.out.se/" },
  { name: "Seaplane · max 3 pax (email Grinda)", email: "info@grinda.se" },
];

export default function HowToPage() {
  return (
    <div data-testid="howto-page">
      <section className="container-x py-12 md:py-20">
        <p className="eyebrow">Stockholm → Grinda</p>
        <h1 className="heading-xl mt-2">
          The journey is <span className="italic-accent">part of it.</span>
        </h1>
        <p className="body-lead mt-3 max-w-2xl">
          Grinda lies in the heart of the Stockholm archipelago. From central Stockholm the boat
          ride takes about an hour; from northern Värmdö as little as ten minutes.
        </p>
      </section>

      {/* Map */}
      <section className="container-x mb-12">
        <div className="relative bg-navy rounded-2xl h-48 md:h-64 overflow-hidden">
          <span className="absolute left-[20%] top-[70%] -translate-y-1/2 bg-cream rounded-round px-3 py-1.5 text-[10px] font-bold tracking-widest uppercase text-navy">
            STOCKHOLM
          </span>
          <span className="absolute right-[15%] top-[20%] bg-coral rounded-round px-3 py-1.5 text-[10px] font-bold tracking-widest uppercase text-cream">
            GRINDA
          </span>
          <span className="absolute left-1/2 top-[45%] w-1/3 h-px bg-sand/50 -translate-x-1/2 rotate-[-22deg]" />
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-black/40 rounded-round px-3 py-1 text-[10px] font-bold tracking-wide text-cream">
            59°24′85″ N · 18°33′37″ E
          </span>
        </div>
        <a
          href="https://www.google.com/maps/dir/?api=1&destination=Grinda+S%C3%B6dra+bryggan,+185+99+Vaxholm"
          target="_blank"
          rel="noreferrer"
          className="btn-primary mt-5"
          data-testid="directions-btn"
        >
          <Navigation size={16} />
          Get directions on Google Maps
        </a>
      </section>

      {/* Public ferries */}
      <section className="container-x mb-10">
        <p className="eyebrow">Scheduled boats</p>
        <h2 className="heading-lg mt-1">Public ferries</h2>
        <p className="body-lead mt-2 max-w-2xl">
          Two operators run regular routes to Grinda from Stockholm and the surrounding archipelago.
        </p>
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {FERRIES.map((f) => (
            <a
              key={f.id}
              href={f.url}
              target="_blank"
              rel="noreferrer"
              className="card p-5 hover:-translate-y-0.5 transition-transform"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-sand text-coral flex items-center justify-center">
                  <Ship size={18} />
                </div>
                <div className="flex-1">
                  <p className="font-serif text-xl text-navy">{f.name}</p>
                  <p className="text-grey text-xs mt-0.5">{f.phone}</p>
                  <p className="text-grey text-sm mt-2 leading-relaxed">{f.blurb}</p>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Two piers */}
      <section className="container-x mb-10">
        <div className="bg-sand rounded-2xl p-5 flex gap-3">
          <Info size={20} className="text-coral shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold tracking-widest uppercase text-navy">
              Two piers — same island
            </p>
            <p className="text-sm text-navy/85 mt-1">
              Boats stop at either <strong>Södra Grinda</strong> (south, busier) or{" "}
              <strong>Norra Grinda</strong> (north). Both are a 10–15 minute walk to Wärdshuset in
              the middle of the island.
            </p>
          </div>
        </div>
      </section>

      {/* Charter options */}
      <section className="container-x mb-12">
        <p className="eyebrow">For groups & VIPs</p>
        <h2 className="heading-lg mt-1">
          Private transfers, <span className="italic-accent">charter & helicopter</span>
        </h2>
        <p className="body-lead mt-2 max-w-2xl">
          Travelling as a company or want a more memorable arrival? Choose from RIB, taxi-boat,
          charter, helicopter or seaplane — even hovercraft in winter.
        </p>
        <div className="mt-6 grid md:grid-cols-2 gap-2.5">
          {CHARTERS.map((c) => (
            <a
              key={c.name}
              href={c.url || `mailto:${c.email}`}
              target="_blank"
              rel="noreferrer"
              className="card p-3 px-4 flex items-center gap-3 text-sm hover:bg-cream/60"
            >
              <span className="text-coral">→</span>
              <span className="flex-1">{c.name}</span>
            </a>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="bg-navy text-cream py-14">
        <div className="container-x flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="text-[11px] font-bold tracking-widest uppercase text-coral">
              Grinda Wärdshus AB
            </p>
            <p className="font-serif text-2xl mt-1">Södra bryggan,</p>
            <p className="font-serif text-2xl">Grinda 185 99 Vaxholm</p>
          </div>
          <div className="flex flex-col gap-2">
            <a
              href="tel:+4685424991"
              className="inline-flex items-center gap-2 text-sand font-bold"
            >
              <Phone size={14} /> +46 8 542 49 491
            </a>
            <a
              href="mailto:info@grinda.se"
              className="inline-flex items-center gap-2 text-sand font-bold"
            >
              <Mail size={14} /> info@grinda.se
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
